<template>
  <div class="item-row" :class="productRowClass" ref="rowRef">
    <template v-if="!hideIfEmpty || products.length > 0">
      <div :class="productRowClass">
        <div v-if="loadFailedError">{{ loadFailedError }}</div>

        <div v-if="!loadFailedError" class="item-row-heading">
          <h2 class="wc-product-row-modern__title" ref="titleRef">
            <span
              class="wc-product-row-modern__title-text"
              :class="{
                'wc-product-row-modern__title-text--hidden':
                  isPersonalized && hasThemeTitleOverride,
              }"
              >{{ rowName }}</span
            >
          </h2>
          <span v-if="isPersonalized" class="wc-product-row-modern__badge">{{
            t('personalized')
          }}</span>
          <router-link
            v-if="totalCount > products.length"
            :to="viewMoreLink"
            class="wc-product-row-modern__view-more-link"
          >
            {{ t('seeAll') }}
          </router-link>
        </div>

        <template v-if="!loadFailedError">
          <div v-if="loading" class="wc-item-row__spinner-wrapper d-flex">
            <WCSpinner class="mx-auto align-self-center" size="xl" />
          </div>
          <template v-if="!loading">
            <WCCarouselModern :class="productRowCarouselClass" :title="rowName" :showArrows="true">
              <WCProductCardModern
                v-for="product in products"
                :key="product.id"
                :item="product"
                class="wc-product-row-modern__card"
                role="listitem"
              />
            </WCCarouselModern>
          </template>
        </template>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import axios from 'axios';
import { computed, defineProps, onBeforeUnmount, onMounted, ref, watch, withDefaults } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import WCProductCardModern from '@/components/WCProductCardModern/WCProductCardModern.vue';
import WCCarouselModern from '@/components/WCCarouselModern/WCCarouselModern.vue';
import WCSpinner from '@/components/WCSpinner/WCSpinner.vue';
import AnalyticsService from '@/services/analytics.service';
import TrackableEventConstants from '@/constants/TrackableEventConstants';
import { ENTRY_METHOD, type EntryMethod } from '@/models/entities/cart/entry-method';
import usePlatform from '@/modules/platform/composables/usePlatform';
import useCardLimitInRow, {
  DEFAULT_CARD_WIDTH_WITH_MARGIN,
  DEFAULT_BUFFER_CARDS,
} from '@/composables/useCardLimitInRow';
import type ItemDetail from '@/models/entities/items/item-detail';

interface Props {
  rowId: string;
  rowName: string;
  rowType?: string;
  rowSizeLimit?: number;
  hideIfEmpty?: boolean;
  entryMethod?: EntryMethod;
  cardWidthWithMargin?: number;
  bufferCards?: number;
  items?: ItemDetail[];
  totalCount?: number;
  seeAllRoute?: string;
  isPersonalized?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  rowType: undefined,
  rowSizeLimit: 7,
  hideIfEmpty: true,
  entryMethod: ENTRY_METHOD.DEFAULT,
  bufferCards: DEFAULT_BUFFER_CARDS,
  items: undefined,
  totalCount: undefined,
  seeAllRoute: undefined,
  isPersonalized: false,
});

const store = useStore();
const loading = ref(false);
const loadFailedError = ref<string | null>(null);
const totalCount = ref(-1);
const products = ref<ItemDetail[]>([]);
const rowRef = ref<HTMLElement | null>(null);
const titleRef = ref<HTMLElement | null>(null);
const hasThemeTitleOverride = ref(false);
const { platform } = usePlatform();
const productRowClass = ref('product-row-class-desktop');
const productRowCarouselClass = ref('wc-product-row-modern__carousel-desktop');
const { t } = useI18n();
const getUser = computed(() => store.getters['user/getUser']);
const isControlled = computed(() => props.items !== undefined);

const viewMoreLink = computed(() => {
  return props.seeAllRoute || `/b?g=${props.rowId}`;
});

const getCardWidthWithMargin = (): number => {
  if (!rowRef.value) {
    return DEFAULT_CARD_WIDTH_WITH_MARGIN;
  }
  const style = getComputedStyle(rowRef.value);
  const cardWidth = parseFloat(style.getPropertyValue('--wc-row-card-width'));
  const gap = parseFloat(style.getPropertyValue('--wc-row-card-gap'));
  return cardWidth && gap ? cardWidth + gap : DEFAULT_CARD_WIDTH_WITH_MARGIN;
};

const getCardLimitInRow = useCardLimitInRow(
  rowRef,
  () => props.rowSizeLimit,
  () => props.cardWidthWithMargin ?? getCardWidthWithMargin(),
  () => props.bufferCards,
);

const fetchData = async (): Promise<void> => {
  const ps = platform.value.isMobile ? props.rowSizeLimit : getCardLimitInRow();
  const req: Record<string, unknown> = {
    ps,
    pn: 1,
  };

  if (props.rowType === 'coupon') {
    req.facets = { cp: [props.rowId] };
  } else {
    req.g = [props.rowId];
  }

  try {
    const response = await axios.post('api/b/', req);
    products.value = response.data.items;
    totalCount.value = response.data.totalCount
      ? response.data.totalCount
      : response.data.items.length;

    AnalyticsService.track(TrackableEventConstants.ItemImpression, {
      items: products.value,
      customerId: getUser.value?.sub,
      listId: props.rowId,
      listName: props.rowName,
    });
  } catch (error) {
    loading.value = false;
    loadFailedError.value = error instanceof Error ? error.message : String(error);
    console.error(error);
  }
};

const updateData = async () => {
  totalCount.value = 0;
  products.value = [];
  loading.value = true;
  loadFailedError.value = null;
  await fetchData();
  loading.value = false;
};

watch(() => props.rowId, () => {
  if (!isControlled.value) {
    updateData();
  }
});

watch(
  () => props.items,
  (newItems) => {
    if (newItems !== undefined) {
      products.value = newItems;
      totalCount.value = props.totalCount ?? newItems.length;
      loading.value = false;
      loadFailedError.value = null;
    }
  },
  { immediate: true },
);

const updateProductRowClass = () => {
  if (platform.value.isMediumDevice) {
    productRowClass.value = 'product-row-class-tablet';
    productRowCarouselClass.value = 'wc-product-row-modern__carousel-tablet';
  } else if (platform.value.isMobile) {
    productRowClass.value = 'product-row-class-mobile';
    productRowCarouselClass.value = 'wc-product-row-modern__carousel-mobile';
  } else {
    productRowClass.value = 'product-row-class-desktop';
    productRowCarouselClass.value = 'wc-product-row-modern__carousel-desktop';
  }
};

// theme.css may inject a title via `h2::after { content: "..." }`.
// When it does, the original span is hidden so it doesn't reserve layout space next to it.
const checkThemeTitleOverride = () => {
  if (!titleRef.value) {
    hasThemeTitleOverride.value = false;
    return;
  }
  const { content } = window.getComputedStyle(titleRef.value, '::after');
  hasThemeTitleOverride.value = Boolean(content) && content !== 'none' && content !== 'normal';
};

if (props.isPersonalized) {
  watch(titleRef, (el) => {
    if (!el) {
      hasThemeTitleOverride.value = false;
      return;
    }
    checkThemeTitleOverride();
  });
}

onMounted(() => {
  if (!isControlled.value) {
    updateData();
  }
  updateProductRowClass();
  window.addEventListener('resize', updateProductRowClass);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateProductRowClass);
});
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-item-row__spinner-wrapper {
  height: 12.5rem;
}

.wc-product-row-modern__card {
  width: var(--wc-row-card-width, 9.375rem);
  display: flex;
  flex-direction: column;
  font-family: var(--font-family);
}

.wc-product-row-modern__title {
  font-size: convert-px-to-rem(20);
  font-weight: 600;
  color: var(--dark);
  line-height: 1.2;
  margin: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-bottom: 0.125rem;
}

.wc-product-row-modern__title-text--hidden {
  display: none;
}

.wc-product-row-modern__badge {
  font-size: 0.625rem;
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.025rem;
  color: var(--white, $white);
  background: var(--info);
  border-radius: 0.25rem;
  padding: 0.0625rem 0.375rem;
  margin-left: 0.375rem;
  flex-shrink: 0;
}

.wc-product-row-modern__view-more-link {
  font-size: 0.875rem;
  font-weight: 400;
  color: var(--primary);
  text-decoration: underline;
  background: none;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  margin-left: auto;
  padding-left: 1rem;
}

.item-row.product-row-class-desktop {
  --wc-row-card-width: 220px;
  --wc-row-card-gap: 16px;
}

.item-row.product-row-class-mobile {
  --wc-row-card-width: 150px;
  --wc-row-card-gap: 12px;
}

.item-row {
  .product-row-class-desktop {
    padding: 0 1.5rem;
    margin-top: 0.875rem;

    .wc-product-row-modern__title {
      font-size: convert-px-to-rem(24);
      font-weight: 600;
    }

    .item-row-heading {
      padding-top: convert-px-to-rem(16);
      padding-bottom: convert-px-to-rem(2);
    }
  }
  .product-row-class-mobile {
    padding: 0;
    margin-top: 0.25rem;
    .item-row-heading {
      padding: 1rem;
      padding-top: convert-px-to-rem(16);
      padding-bottom: convert-px-to-rem(2);
    }
  }
  .product-row-class-tablet {
    padding: 0 2rem !important;
  }
  .item-row-heading {
    display: flex;
    align-items: center;
    padding-top: convert-px-to-rem(16);
    width: 100%;
  }
}

.wc-product-row-modern__carousel-desktop {
  margin-left: -0.625rem;
  width: calc(100% + 0.625rem);

  :deep(.wc-carousel-modern__track) {
    padding: 0.625rem 1rem 0.75rem 0.625rem !important;
    margin: 0 !important;
  }

  @media (hover: hover) and (pointer: fine) {
    :deep(.wc-carousel-modern__track) {
      padding-top: 0.75rem !important;
    }
  }
}

.wc-product-row-modern__carousel-mobile {
  :deep(.wc-carousel-modern__track) {
    padding: 0.75rem 1rem !important;
    margin: 0 !important;
  }
}

.wc-product-row-modern__carousel-tablet {
  :deep(.wc-carousel-modern__track) {
    padding: 0.75rem 0 !important;
    margin: 0 !important;
  }
}
</style>
